/**
 * Who may open the salon showcase.
 *
 * The decision is made by the SERVER: `GET /t/{slug}/api/v1/admin/shell-context`
 * is only answered for an authenticated member of this tenant (ADR-001 §2.2
 * steps 5–6) and carries `tenantApps`, derived from the tenant's own plan.
 * The client only reads that answer; it never decides entitlement from the
 * slug, the URL or anything it stores. Anything short of an explicit grant —
 * missing field, wrong app, malformed payload — is a denial.
 */
import type { ShellContextResponse } from '../../lib/types/shell.ts';
import { salonNamespace } from './engine/persistence.ts';

export const SALON_APP_SLUG = 'salon-showcase';

export type SalonAccess =
  { readonly kind: 'granted'; readonly principalId: string } | { readonly kind: 'denied' };

export function evaluateSalonAccess(ctx: ShellContextResponse | null | undefined): SalonAccess {
  if (!ctx || !Array.isArray(ctx.tenantApps) || !ctx.tenantApps.includes(SALON_APP_SLUG)) {
    return { kind: 'denied' };
  }
  const principalId = ctx.principalId;
  if (typeof principalId !== 'string' || principalId.length === 0) return { kind: 'denied' };
  return { kind: 'granted', principalId };
}

/** Browser-storage namespace for one tenant + one account. The slug is immutable (ADR-001 §2.1). */
export const salonStorageNamespace = (slug: string, principalId: string): string =>
  salonNamespace(slug, principalId);
